import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  ActionType,
  convertToISO,
  DayIntervalUnit,
  FeatureName,
  parseMMDDYYYYToDate,
  RoutineType,
  RoutineWorkflow,
  TriggerType,
  useIsFeatureEnabled,
  WorkspaceAsset,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { router } from 'expo-router';
import moment from 'moment';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { number, string, object as yupObject } from 'yup';
import { Colors } from '../../../themes/colors';
import { Button } from '../../crop-circle/Button/Button';
import { Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import { fontWeights } from '../../crop-circle/tokens/typography';
import { ControlledSelect, FormContext } from '../../forms';
import { KeyboardSafeView } from '../../keyboard/keyboardSafeView';
import { BottomComponentBar } from '../../navigation';
import { AlertFormOld } from './alertForm-old';
import AlertLogicPicker from './alertLogicPicker';
import {
  DROPDOWN_MAX_HEIGHT,
  getCropCircleDropDownPickerProps,
} from './cropCircleAlertDropdown';
import { AlertInputs, AlertType } from './types';

type AlertFormProps = {
  defaultValues: any;
  routine?: WorkspaceRoutine;
  workspaceAsset?: WorkspaceAsset;
  product?: { id: string; workspace?: { id: string } };
  group?: { id: string; workspace?: { id: string } };
  counterRoutines: WorkspaceRoutine[];
  availableRoutines?: WorkspaceRoutine[];
  workflowProps?: RoutineWorkflow;
  isEdit?: boolean;
  headerTitle?: string;
  backButtonLabel?: string;
  openDropdownOnLoad?: boolean;
  renderBottomComponent?: () => React.ReactNode; // For custom bottom component (e.g., SlideLayout confirm button)
  /** Ref to get handleSubmit and reset from outside (avoids putting confirm button inside form and triggering max update depth) */
  formRef?: React.MutableRefObject<{ handleSubmit: (fn: (data: any) => Promise<void>) => () => void; reset: () => void } | null>;
  /** Called when isValid/isSubmitting/isValidating change so parent can drive a confirm button without form context */
  onFormStateChange?: (state: { isValid: boolean; isSubmitting: boolean; isValidating: boolean }) => void;
  submitAlert: (input: any) => Promise<void>;
  deleteAlert?: () => Promise<void>;
  onClose?: () => void;
};

// TODO: Crop Circle Redesign: Remove after feature is fully rolled out
export const AlertForm = (props: AlertFormProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  if (!isCropCircleRedesignEnabled) {
    return <AlertFormOld {...props} />;
  }
  return <AlertFormBase {...props} />;
};

const AlertFormBase = ({
  defaultValues,
  routine,
  workspaceAsset,
  product,
  group,
  counterRoutines,
  availableRoutines,
  workflowProps,
  isEdit = false,
  headerTitle,
  backButtonLabel,
  openDropdownOnLoad,
  renderBottomComponent,
  formRef,
  onFormStateChange,
  submitAlert,
  deleteAlert,
  onClose,
}: AlertFormProps) => {
  const { t } = useTranslation();
  const [additionalLogic, setAdditionalLogic] = useState(false);
  const [showRoutinesDropdown, setShowRoutinesDropdown] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const workspaceId =
    workspaceAsset?.workspace?.id ||
    product?.workspace?.id ||
    group?.workspace?.id;
  const workspaceAssetId = workspaceAsset?.id;
  const productId = product?.id;
  const groupId = group?.id;
  const MIN_REMINDER_MINUTES = 15;

  const isValidDate = (value?: string) => {
    if (!value) return true;
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    if (!regex.test(value)) return false;
    const [month, day, year] = value.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  const formSchema = yupObject({
    selectedRoutine: string().optional(),
    alertType: string()
      .required()
      .min(1, t('alerts.validation.alertTypeRequired')),
    inputField: string()
      .required()
      .min(1, t('alerts.validation.inputRequired')),
    secondAlertType: string().optional(),
    secondInputField: string()
      .required()
      .when('secondAlertType', {
        is: (val: any) => val !== undefined,
        otherwise: (schema) => schema.optional(),
      }),
    alertLabel: string().optional(),
    alertLabelPlaceholder: string().optional(),
    startDate: string()
      .optional()
      .test('is-valid-date', t('alerts.validation.invalidDate'), isValidDate),
    endDate: string()
      .optional()
      .test('is-valid-date', t('alerts.validation.invalidDate'), isValidDate),
    minValue: number().optional(),
    maxValue: number().optional(),
    daysStartDate: string()
      .nullable()
      .when(['alertType', 'secondAlertType'], {
        is: (val: string) =>
          val === AlertType.RecurringInterval ||
          secondAlertType === AlertType.RecurringInterval,
        then: (schema) =>
          schema.required(t('alerts.validation.startDateRequiredForDays')),
        otherwise: (schema) => schema.optional(),
      }),
    completions: number().optional(),
    daysSkipped: number().optional(),
    onDate: string().optional(),
    dayInterval: string().when(['alertType', 'dayIntervalUnit'], {
      is: (alertType: string, dayIntervalUnit: string) => {
        return (
          alertType === AlertType.RecurringInterval &&
          dayIntervalUnit === DayIntervalUnit.Minutes
        );
      },
      then: (schema) =>
        schema
          .required('Interval is required')
          .test('min-minutes', 'Must be at least 15 minutes', (value) => {
            const numValue = parseInt(value || '0', 10);
            return numValue >= MIN_REMINDER_MINUTES;
          }),
      otherwise: (schema) =>
        schema.when('alertType', {
          is: (val: string) => val === AlertType.RecurringInterval,
          then: (schema) =>
            schema
              .required('Interval is required')
              .test('min-interval', 'Must be at least 1', (value) => {
                const numValue = parseInt(value || '0', 10);
                return numValue >= 1;
              }),
          otherwise: (schema) => schema.optional(),
        }),
    }),
    dayIntervalUnit: string().when('alertType', {
      is: (val: string) => val === AlertType.RecurringInterval,
      then: (schema) => schema.required('Interval unit is required'),
      otherwise: (schema) => schema.optional(),
    }),
    onInterval: number().optional(),
    onIntervalStart: number().optional(),
    intervalRoutineId: string()
      .nullable()
      .when(['alertType', 'secondAlertType'], {
        is: (alertType: string, secondType: string) => {
          const counterBasedTypes = [
            AlertType.Interval,
            AlertType.Min,
            AlertType.Max,
          ];
          return (
            counterBasedTypes.includes(alertType as AlertType) ||
            counterBasedTypes.includes(secondType as AlertType)
          );
        },
        then: (schema) =>
          schema.required(t('alerts.validation.routineIdRequiredForInterval')),
        otherwise: (schema) => schema.optional(),
      }),
  });

  // Create: onTouched so isValid updates when user fills fields (Confirm disabled until valid).
  // Edit: defaultValues so form is editable and confirm/reset work (parent uses key={alertId} so we get correct initial data per alert).
  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: isEdit ? 'onChange' : 'onTouched',
    defaultValues: defaultValues ?? {},
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid, isSubmitting, isValidating },
    setValue,
    getValues,
  } = formMethods;

  // Expose submit and reset to parent ref (useLayoutEffect so ref is set before header can be used)
  const resetToDefaults = useCallback(() => {
    const values = defaultValues ?? {};
    reset(values);
    setAdditionalLogic(!!values.secondAlertType);
    setShowRoutinesDropdown(false);
    setIsDropdownOpen(false);
  }, [reset, defaultValues]);

  useLayoutEffect(() => {
    if (!formRef) return;
    formRef.current = { handleSubmit, reset: resetToDefaults };
    return () => {
      formRef.current = null;
    };
  }, [formRef, handleSubmit, resetToDefaults]);

  // Notify parent only when validity/loading change. Defer to next tick to avoid
  // "Cannot update a component while rendering a different component" (RHF validation runs during Controller render).
  const prevState = React.useRef<{ isValid: boolean; isSubmitting: boolean; isValidating: boolean } | null>(null);
  useEffect(() => {
    if (!onFormStateChange) return;
    const next = { isValid, isSubmitting, isValidating };
    if (
      prevState.current &&
      prevState.current.isValid === next.isValid &&
      prevState.current.isSubmitting === next.isSubmitting &&
      prevState.current.isValidating === next.isValidating
    ) {
      return;
    }
    prevState.current = next;
    const timeoutId = setTimeout(() => onFormStateChange(next), 0);
    return () => clearTimeout(timeoutId);
  }, [isValid, isSubmitting, isValidating, onFormStateChange]);

  const alertType = useWatch({ control, name: 'alertType' });
  const selectedRoutineId = useWatch({ control, name: 'selectedRoutine' });

  // Use selected routine from form, fallback to passed routine
  const currentRoutine =
    availableRoutines && selectedRoutineId
      ? availableRoutines.find((r) => r.id === selectedRoutineId) || routine
      : routine;

  const workspaceRoutineId = currentRoutine?.id;
  const routineType = currentRoutine?.type;
  const routineName = currentRoutine?.config?.routineButtonText;
  const defaultAlertName = `${t('alerts.alert')}: ${routineName}`;

  const isCounterRoutine = useMemo(() => {
    return routineType === RoutineType.Counter;
  }, [routineType]);

  const secondAlertType = useWatch({ control, name: 'secondAlertType' });
  const secondInputField = useWatch({ control, name: 'secondInputField' });
  const alertName = useWatch({ control, name: AlertInputs.alertLabel });
  const alertNamePlaceholder = useWatch({
    control,
    name: AlertInputs.alertLabelPlaceholder,
  });

  const fromDate = useWatch({ control, name: AlertInputs.startDate });
  const toDate = useWatch({ control, name: AlertInputs.endDate });
  const minCounterValue = useWatch({ control, name: AlertInputs.minValue });
  const maxCounterValue = useWatch({ control, name: AlertInputs.maxValue });
  const daysStartDate = useWatch({ control, name: AlertInputs.daysStartDate });
  const completionsValue = useWatch({ control, name: AlertInputs.completions });
  const daysSkippedValue = useWatch({ control, name: AlertInputs.daysSkipped });
  const onDateValue = useWatch({ control, name: AlertInputs.onDate });
  const dayIntervalValue = useWatch({ control, name: AlertInputs.dayInterval });
  const dayIntervalUnitValue = useWatch({
    control,
    name: AlertInputs.dayIntervalUnit,
  });

  const intervalRoutineIdValue = useWatch({
    control,
    name: AlertInputs.intervalRoutineId,
  });
  const onIntervalValue = useWatch({ control, name: AlertInputs.onInterval });
  const onIntervalStart = useWatch({
    control,
    name: AlertInputs.onIntervalStart,
  });

  const getAlertName = useCallback(
    (alertType: AlertType, isSecondAlert: boolean) => {
      let alertValue;

      switch (alertType) {
        case AlertType.Date:
          alertValue = getValues(AlertInputs.onDate);
          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${t('alerts.on')} ${alertValue}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${t('alerts.on')} ${alertValue}`
            : `${defaultAlertName}`;
        case AlertType.DaysSkipped:
          alertValue = getValues(AlertInputs.daysSkipped);
          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${alertValue} ${t('alerts.days')} ${t(
                  'alerts.skipped'
                )}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${alertValue} ${t('alerts.days')} ${t(
                'alerts.skipped'
              )}`
            : `${defaultAlertName}`;
        case AlertType.RoutineCompletions:
          alertValue = getValues(AlertInputs.completions);
          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${alertValue} ${t('alerts.completions')}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${alertValue} ${t('alerts.completions')}`
            : `${defaultAlertName}`;

        case AlertType.Min:
          alertValue = getValues(AlertInputs.minValue);
          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${t(
                  'alerts.abovePlaceholder'
                )} ${alertValue}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${t(
                'alerts.abovePlaceholder'
              )} ${alertValue}`
            : `${defaultAlertName}`;

        case AlertType.Max:
          alertValue = getValues(AlertInputs.maxValue);
          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${t(
                  'alerts.belowPlaceholder'
                )} ${alertValue}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${t(
                'alerts.belowPlaceholder'
              )} ${alertValue}`
            : `${defaultAlertName}`;

        case AlertType.Interval: {
          alertValue = getValues(AlertInputs.onInterval);
          const routineId = getValues(AlertInputs.intervalRoutineId);
          const uom = routineId
            ? counterRoutines.find((r) => r.id === routineId)?.config.uom
            : '';

          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${t('alerts.every')} ${alertValue} ${uom}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${t('alerts.every')} ${alertValue} ${uom}`
            : `${defaultAlertName}`;
        }
        case AlertType.RecurringInterval: {
          const intervalValue = getValues(AlertInputs.dayInterval);
          const unitValue = getValues(AlertInputs.dayIntervalUnit);
          const unitLabel = unitValue
            ? t(`alerts.${unitValue.toLowerCase()}`, unitValue.toLowerCase())
            : '';

          if (isSecondAlert) {
            return intervalValue && unitValue
              ? ` ${t('alerts.or')} ${t(
                  'alerts.every'
                )} ${intervalValue} ${unitLabel}`
              : '';
          }
          return intervalValue && unitValue
            ? `${defaultAlertName}, ${t(
                'alerts.every'
              )} ${intervalValue} ${unitLabel}`
            : `${defaultAlertName}`;
        }
        default:
          return `${defaultAlertName}`;
      }
    },
    [counterRoutines, defaultAlertName, getValues, t]
  );

  // Programmatic sync of inputs for alert type; use shouldDirty: false so edit form isn't marked dirty on load
  const updateFormInputField = useCallback(
    (inputName: string, alertType: AlertType) => {
      const opts = { shouldDirty: false, shouldValidate: true };
      switch (alertType) {
        case AlertType.Date:
          setValue(inputName, onDateValue, opts);
          break;
        case AlertType.DaysSkipped:
          setValue(inputName, daysSkippedValue, opts);
          break;
        case AlertType.RoutineCompletions:
          setValue(inputName, completionsValue, opts);
          break;
        case AlertType.Min:
          setValue(inputName, minCounterValue, opts);
          break;
        case AlertType.Max:
          setValue(inputName, maxCounterValue, opts);
          break;
        case AlertType.Interval:
          setValue(AlertInputs.intervalRoutineId, intervalRoutineIdValue, opts);
          setShowRoutinesDropdown(true);
          setValue(inputName, onIntervalValue, opts);
          break;
        case AlertType.RecurringInterval:
          setValue(AlertInputs.dayInterval, dayIntervalValue, opts);
          setValue(AlertInputs.dayIntervalUnit, dayIntervalUnitValue, opts);
          setValue(AlertInputs.daysStartDate, daysStartDate, opts);
          setValue(inputName, dayIntervalValue, opts);
          break;

        default:
          break;
      }
    },
    [
      completionsValue,
      daysSkippedValue,
      daysStartDate,
      dayIntervalValue,
      dayIntervalUnitValue,
      intervalRoutineIdValue,
      maxCounterValue,
      minCounterValue,
      onDateValue,
      onIntervalValue,
      setValue,
    ]
  );

  const counterRoutineItems = useMemo(() => {
    if (!counterRoutines || !counterRoutines.length) return [];

    return counterRoutines.map((routine) => {
      const { config, id } = routine;

      return {
        label: `${t('alerts.Every')} ___ ${config.uom}`,
        value: id,
        count: undefined, // Counter routines don't have state.count, might come from config
        type: AlertInputs.onIntervalStart,
        uom: config.uom,
      };
    });
  }, [counterRoutines, t]);

  // update first inputField
  useEffect(() => {
    if (!alertType) return;

    updateFormInputField('inputField', alertType);

    // update custom label input
    const updatedName = getAlertName(alertType, false);
    setValue(AlertInputs.alertLabelPlaceholder, updatedName, {
      shouldDirty: false,
    });
  }, [
    alertType,
    minCounterValue,
    maxCounterValue,
    daysStartDate,
    completionsValue,
    daysSkippedValue,
    dayIntervalValue,
    dayIntervalUnitValue,
    intervalRoutineIdValue,
    onDateValue,
    onIntervalValue,
    updateFormInputField,
    getAlertName,
    setValue,
  ]);

  // update second inputField
  useEffect(() => {
    if (!secondAlertType) return;
    updateFormInputField('secondInputField', secondAlertType);

    // update placeholder label
    const currentPlaceholder = getValues(AlertInputs.alertLabelPlaceholder);
    const strToAppend = getAlertName(secondAlertType, true);

    setValue(
      AlertInputs.alertLabelPlaceholder,
      currentPlaceholder + strToAppend,
      { shouldDirty: false }
    );
  }, [
    secondAlertType,
    minCounterValue,
    maxCounterValue,
    daysStartDate,
    completionsValue,
    daysSkippedValue,
    dayIntervalValue,
    dayIntervalUnitValue,
    intervalRoutineIdValue,
    onDateValue,
    onIntervalValue,
    updateFormInputField,
    getValues,
    getAlertName,
    setValue,
  ]);

  // reset form on render and every time a user selects from drop picker
  useEffect(() => {
    // hide routines interval dropdown if not needed (only set state when changing to avoid infinite loop)
    const counterBasedTypes = [
      AlertType.Interval,
      AlertType.Min,
      AlertType.Max,
    ];
    if (!counterBasedTypes.includes(alertType as AlertType)) {
      setShowRoutinesDropdown((prev) => (prev ? false : prev));
    }

    if (isEdit) {
      const defaultAlertType = defaultValues.alertType;
      // Only reset when user changes alert type; do not unconditionally reset on every run
      if (alertType !== defaultAlertType) {
        reset({
          ...getValues(), // leave out secondAlertType
          secondInputField: secondInputField || defaultValues.secondInputField,
          [AlertInputs.onDate]: undefined,
          [AlertInputs.daysSkipped]: undefined,
          [AlertInputs.completions]: undefined,
          [AlertInputs.daysStartDate]: defaultValues.daysStartDate,
          [AlertInputs.maxValue]: undefined,
          [AlertInputs.minValue]: undefined,
          [AlertInputs.alertLabel]: '',
          [AlertInputs.alertLabelPlaceholder]: defaultAlertName,
          [AlertInputs.startDate]: undefined,
          [AlertInputs.endDate]: undefined,
          [AlertInputs.onInterval]: undefined,
          [AlertInputs.intervalRoutineId]: undefined,
          [AlertInputs.onIntervalStart]: undefined,
          [AlertInputs.dayInterval]: undefined,
          [AlertInputs.dayIntervalUnit]: undefined,
        });
      }
      return;
    }

    reset({
      ...getValues(), // leave out alertType
      inputField: undefined,
      [AlertInputs.onDate]: undefined,
      [AlertInputs.daysSkipped]: undefined,
      [AlertInputs.completions]: undefined,
      [AlertInputs.daysStartDate]: defaultValues.daysStartDate,
      [AlertInputs.maxValue]: undefined,
      [AlertInputs.minValue]: undefined,
      [AlertInputs.alertLabel]: '',
      [AlertInputs.alertLabelPlaceholder]: defaultAlertName,
      [AlertInputs.startDate]: undefined,
      [AlertInputs.endDate]: undefined,
      [AlertInputs.onInterval]: undefined,
      [AlertInputs.intervalRoutineId]: undefined,
      [AlertInputs.onIntervalStart]: undefined,
      [AlertInputs.dayInterval]: undefined,
      [AlertInputs.dayIntervalUnit]: undefined,
    });
  }, [
    alertType,
    isEdit,
    defaultValues,
    reset,
    getValues,
    defaultAlertName,
    secondInputField,
  ]);

  const defaultRoutineConfig = useMemo(() => {
    const config = {
      workspaceRoutineId,
      daysSinceCompleted: daysSkippedValue,
      onInterval: onIntervalValue,
      onIntervalStart: onIntervalStart,
      triggerCounterRoutineId: intervalRoutineIdValue,
      startDate:
        dayIntervalValue || daysSkippedValue
          ? parseMMDDYYYYToDate(daysStartDate)
          : undefined,
      onCompletionCount: completionsValue,
      onDate: parseMMDDYYYYToDate(onDateValue),
      dayInterval: dayIntervalValue
        ? parseInt(dayIntervalValue, 10)
        : undefined,
      dayIntervalUnit: dayIntervalUnitValue,
      routineType,
    };
    return Object.fromEntries(
      Object.entries(config).filter(([_, v]) => v !== undefined)
    );
  }, [
    workspaceRoutineId,
    routineType,
    completionsValue,
    daysSkippedValue,
    daysStartDate,
    onDateValue,
    dayIntervalValue,
    dayIntervalUnitValue,
    onIntervalValue,
    onIntervalStart,
    intervalRoutineIdValue,
  ]);

  const counterRoutineConfig = useMemo(() => {
    const config = {
      onInterval: onIntervalValue,
      onIntervalStart: onIntervalStart,
      triggerCounterRoutineId: intervalRoutineIdValue,
      ceiling: minCounterValue,
      floor: maxCounterValue,
      workspaceRoutineId,
      daysSinceCompleted: daysSkippedValue,
      startDate:
        dayIntervalValue || daysSkippedValue
          ? parseMMDDYYYYToDate(daysStartDate)
          : undefined,
      onCompletionCount: completionsValue,
      onDate: parseMMDDYYYYToDate(onDateValue),
      dayInterval: dayIntervalValue
        ? parseInt(dayIntervalValue, 10)
        : undefined,
      dayIntervalUnit: dayIntervalUnitValue,
      routineType,
    };

    return Object.fromEntries(
      Object.entries(config).filter(([_, v]) => v !== undefined)
    );
  }, [
    routineType,
    workspaceRoutineId,
    minCounterValue,
    maxCounterValue,
    daysStartDate,
    completionsValue,
    daysSkippedValue,
    onDateValue,
    dayIntervalValue,
    dayIntervalUnitValue,
    onIntervalValue,
    onIntervalStart,
    intervalRoutineIdValue,
  ]);

  const onSubmit = async (data: any) => {
    const name = alertName || alertNamePlaceholder;
    const workspace = { id: workspaceId };
    const workspaceAsset = { id: workspaceAssetId };
    const workflowId = workflowProps?.id;
    const trigger = {
      type: TriggerType.Routine,
      config: counterRoutineConfig
        ? counterRoutineConfig
        : defaultRoutineConfig,
    };

    if (trigger.config.dayIntervalUnit && trigger.config.startDate) {
      const now = moment();
      const startDate = moment(trigger.config.startDate);

      // Preserve the date but include current time information
      let adjustedStartDate = startDate;

      switch (trigger.config.dayIntervalUnit) {
        case DayIntervalUnit.Minutes:
        case DayIntervalUnit.Hours:
          adjustedStartDate = startDate
            .hour(now.hour())
            .minute(now.minute())
            .second(now.second());
          break;
      }

      trigger.config.startDate = adjustedStartDate.toISOString();
    }

    const action = {
      type: ActionType.Alert,
      config: { promptWorkspaceRoutineId: workspaceRoutineId },
    };
    const disabledBetween =
      fromDate && toDate
        ? { start: convertToISO(fromDate), end: convertToISO(toDate) }
        : undefined;

    const newAlertInput = {
      name: name,
      workspace: workspace,
      ...(workspaceAssetId && { workspaceAsset: workspaceAsset }),
      ...(productId && { product: { id: productId } }),
      ...(groupId && { group: { id: groupId } }),
      trigger: trigger,
      action: action,
      disabledBetween: disabledBetween,
    };

    const editAlertInput = {
      id: workflowId,
      name: name,
      trigger: trigger,
      action: action,
      disabledBetween: disabledBetween,
    };

    await submitAlert(isEdit ? editAlertInput : newAlertInput);
    reset();
  };

  const isWeb = Platform.OS === 'web';

  const confirmDisabled = isSubmitting || !(isDirty && isValid);
  // Desktop: disable until valid; in edit also require changes (isDirty)
  const desktopConfirmDisabled =
    isSubmitting || isValidating || !isValid || (isEdit && !isDirty);

  // Defer setState so picker/dropdown don't trigger update-during-render. Use setTimeout (not requestAnimationFrame) to avoid "Illegal invocation" on web.
  const defer = useCallback((fn: () => void) => {
    setTimeout(fn, 0);
  }, []);
  const setAdditionalLogicDeferred = useCallback(
    (value: boolean) => defer(() => setAdditionalLogic(value)),
    [defer]
  );
  const setShowRoutinesDropdownDeferred = useCallback(
    (value: boolean) => defer(() => setShowRoutinesDropdown(value)),
    [defer]
  );
  const onDropdownStateChangeDeferred = useCallback(
    (isOpen: boolean) => defer(() => setIsDropdownOpen(isOpen)),
    [defer]
  );

  const cropCircleBackButton = (
    <Button
      variant="rounded"
      state="active"
      onPress={() => {
        if (Platform.OS === 'web' && onClose) {
          onClose();
        } else if (router.canGoBack()) {
          router.back();
        } else {
          router.replace('/');
        }
      }}
      style={styles.cropCircleBackButton}
      leftIcon={<Icon name="Chevron Left" size={24} color={Colors.white} />}
    />
  );

  const cropCircleResetButton = (
    <Button
      variant="rounded"
      state="active"
      onPress={() => reset()}
      style={styles.cropCircleResetButton}
      leftIcon={<Icon name="Reset" size={24} color={Colors.white} />}
    />
  );

  return (
    <FormContext.Provider value={formMethods}>
      {isWeb ? (
        <View style={styles.webRoot}>
          <View style={[styles.container, styles.webScrollWrap, { backgroundColor: tokens.colors.neutral[100] }]}>
            <ScrollView
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.scrollViewContainer}
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled={true}
            >
              {availableRoutines && availableRoutines.length > 0 && (
                <View style={styles.routineSelectionContainer}>
                  <Text style={styles.routineSelectionLabel}>
                    {t('alerts.selectRoutineToPrompt')}
                  </Text>
                  <ControlledSelect
                    name="selectedRoutine"
                    control={control}
                    label={t('alerts.selectRoutineToPrompt')}
                    items={availableRoutines.map((routine) => ({
                      label:
                        routine.name ||
                        routine.config?.routineButtonText ||
                        'Unnamed Routine',
                      value: routine.id,
                    }))}
                    disabled={true}
                    onSelectItem={(item: string | null) => {
                      if (item && typeof item === 'string') {
                        setValue('selectedRoutine', item, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                    dropDownPickerProps={{
                      ArrowDownIconComponent: () => (
                        <MaterialCommunityIcons name="chevron-down" size={28} />
                      ),
                      ArrowUpIconComponent: () => (
                        <MaterialCommunityIcons name="chevron-up" size={28} />
                      ),
                      style: styles.routineDropdown,
                      placeholderStyle: styles.routinePlaceholderLabel,
                    }}
                  />
                </View>
              )}
              {counterRoutineItems && (
                <AlertLogicPicker
                  control={control}
                  alertType={alertType}
                  secondAlertType={secondAlertType}
                  additionalLogic={additionalLogic}
                  isCounterRoutine={isCounterRoutine}
                  setAdditionalLogic={setAdditionalLogicDeferred}
                  showRoutinesDropdown={showRoutinesDropdown}
                  setShowRoutinesDropdown={setShowRoutinesDropdownDeferred}
                  counterRoutineItems={counterRoutineItems}
                  openDropdownOnLoad={openDropdownOnLoad}
                />
              )}
              {isEdit && (
                <Pressable
                  onPress={deleteAlert}
                  hitSlop={5}
                  style={styles.deleteAlertButton}
                >
                  <Text allowFontScaling={false} style={styles.deleteLabel}>
                    {t('alerts.deleteAlert')}
                  </Text>
                </Pressable>
              )}
            </ScrollView>
          </View>
          {renderBottomComponent ? renderBottomComponent() : (
            <BottomComponentBar
              backButton={cropCircleBackButton}
              onSave={handleSubmit(onSubmit)}
              rightPressDisabled={confirmDisabled}
              saveLabel={t('alerts.confirm')}
              right={cropCircleResetButton}
            />
          )}
        </View>
      ) : (
        <>
          <View style={styles.cropCircleHeader}>
            <Text style={styles.cropCircleHeaderText}>
              {isEdit ? t('alerts.editAlert') : t('alerts.newAlert')}
            </Text>
          </View>
          <KeyboardSafeView style={styles.cropCircleContainer}>
            <ScrollView
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={[
                styles.cropCircleScrollContent,
                isDropdownOpen && {
                  paddingBottom: 120 + DROPDOWN_MAX_HEIGHT,
                },
              ]}
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled={true}
            >
              {availableRoutines && availableRoutines.length > 0 && Platform.OS === 'web' && (
                <View style={styles.cropCircleSection}>
                  <Text style={styles.cropCircleSectionLabel}>
                    {t('alerts.selectRoutineToPrompt')}
                  </Text>
                  <ControlledSelect
                    name="selectedRoutine"
                    control={control}
                    label={t('alerts.selectRoutineToPrompt')}
                    items={availableRoutines.map((routine) => ({
                      label:
                        routine.name ||
                        routine.config?.routineButtonText ||
                        'Unnamed Routine',
                      value: routine.id,
                    }))}
                    disabled={true}
                    onSelectItem={(item: string | null) => {
                      if (item && typeof item === 'string') {
                        setValue('selectedRoutine', item, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                    dropDownPickerProps={getCropCircleDropDownPickerProps()}
                  />
                </View>
              )}
              {counterRoutineItems && (
                <View style={styles.cropCircleSection}>
                  <Text style={styles.cropCircleSectionLabel}>
                    {t('alerts.setAlertLogic')}
                  </Text>
                  <AlertLogicPicker
                    control={control}
                    alertType={alertType}
                    secondAlertType={secondAlertType}
                    additionalLogic={additionalLogic}
                    isCounterRoutine={isCounterRoutine}
                    setAdditionalLogic={setAdditionalLogicDeferred}
                    showRoutinesDropdown={showRoutinesDropdown}
                    setShowRoutinesDropdown={setShowRoutinesDropdownDeferred}
                    counterRoutineItems={counterRoutineItems}
                    openDropdownOnLoad={openDropdownOnLoad}
                    onDropdownStateChange={onDropdownStateChangeDeferred}
                  />
                </View>
              )}
              {isEdit && (
                <Button
                  onPress={deleteAlert}
                  state="outline"
                  variant="rounded"
                  style={{ marginTop: tokens.spacing.lg }}
                >
                  {t('alerts.deleteAlert')}
                </Button>
              )}
            </ScrollView>
          </KeyboardSafeView>
          <BottomComponentBar
            backButton={cropCircleBackButton}
            onSave={handleSubmit(onSubmit)}
            rightPressDisabled={confirmDisabled}
            saveLabel={t('alerts.confirm')}
            right={cropCircleResetButton}
          />
        </>
      )}
    </FormContext.Provider>
  );
};

const styles = StyleSheet.create({
  webRoot: {
    flex: 1,
    minHeight: 0,
  },
  webScrollWrap: {
    flex: 1,
    minHeight: 0,
  },
  container: {
    flex: 1,
    flexGrow: 1,
    backgroundColor: Colors.primaryGrayBackground,
    padding: 20,
  },
  scrollViewContainer: {
    flexGrow: 1,
    paddingBottom: 400, // Increased padding to ensure date picker doesn't hide fields
  },
  routineSelectionContainer: {
    marginBottom: 20,
    backgroundColor: Colors.white,
    borderRadius: 8,
    padding: 16,
    zIndex: 1000,
    elevation: 1000,
  },
  routineSelectionLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.black,
    marginBottom: 8,
  },
  routineDropdown: {
    borderWidth: 1,
    borderColor: Colors.grayText,
    height: 52,
    fontSize: 16,
    fontWeight: '400',
  },
  routinePlaceholderLabel: {
    fontSize: 16,
    fontWeight: '400',
    color: Colors.grayFill,
  },
  deleteAlertButton: {
    width: 100,
    height: 25,
    marginTop: 20,
  },
  deleteLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.red,
    letterSpacing: -0.25,
  },
  // Crop circle redesign styles
  cropCircleHeader: {
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.lg,
    paddingBottom: tokens.spacing.md,
    backgroundColor: tokens.colors.neutral[100],
  },
  cropCircleHeaderText: {
    ...tokens.typography.body.medium,
    fontWeight: fontWeights.semibold,
    color: tokens.colors.brand.black,
  },
  cropCircleContainer: {
    flex: 1,
    flexGrow: 1,
    backgroundColor: tokens.colors.neutral[100],
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.sm,
  },
  cropCircleScrollContent: {
    flexGrow: 1,
    paddingBottom: 120,
  },
  cropCircleSection: {
    marginBottom: tokens.spacing.lg,
  },
  cropCircleSectionLabel: {
    ...tokens.typography.body.small,
    fontWeight: fontWeights.semibold,
    color: tokens.colors.brand.black,
    marginBottom: tokens.spacing.sm,
  },
  cropCircleBackButton: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
    marginLeft: 16,
  },
  cropCircleResetButton: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
  },
});
